import { useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Globe,
  Search,
  Check,
  Clock,
  AlertTriangle,
  ExternalLink,
  Copy,
  CheckCheck,
  Cloud,
  RefreshCw,
} from 'lucide-react';
import { api, ApiError, type ManagedDomain } from '../api/client';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/Toast';
import { cn } from '../lib/cn';

type Tab = 'all' | 'available' | 'in-use';

interface DomainRow {
  rootDomain: string;
  source: 'managed' | 'project';
  managed?: ManagedDomain;
  inUseByProjectId?: string;
  inUseByProjectName?: string;
  attached: boolean;
}

export function DomainsScreen() {
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [syncing, setSyncing] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  // Source 1 — managed domains the user owns and can connect to a project.
  const { data: availableData, isLoading, error } = useQuery({
    queryKey: ['allDomains'],
    queryFn: () => api.allDomains(),
    refetchInterval: 10000,
  });
  const managedDomains = availableData?.domains ?? [];

  async function handleSync(): Promise<void> {
    setSyncing(true);
    try {
      const res = await api.syncDomains();
      const newlyAdded = res.upserts.filter((u) => u.created).length;
      const total = res.zonesFound;
      toast.success(
        newlyAdded > 0
          ? `Synced ${total} zones from Cloudflare · ${newlyAdded} newly added`
          : `Synced ${total} zones from Cloudflare · no new domains`,
      );
      await qc.invalidateQueries({ queryKey: ['allDomains'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error(`Cloudflare sync failed: ${msg}`);
    } finally {
      setSyncing(false);
    }
  }

  // Source 2 — projects (each project's liveUrl reveals an in-use domain).
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    refetchInterval: 10000,
  });
  const projects = projectsData?.projects ?? [];

  // Fetch per-project state so we can read each liveUrl. Cheap — react-query
  // de-dupes against the rest of the app's polling.
  const stateQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: ['projectState', p.id],
      queryFn: () => api.getProjectState(p.id),
      refetchInterval: 10000,
    })),
  });

  // Build the unified row list.
  const rows = useMemo<DomainRow[]>(() => {
    const byHost = new Map<string, DomainRow>();

    // First pass — every managed (CF-owned) domain.
    for (const m of managedDomains) {
      byHost.set(m.rootDomain.toLowerCase(), {
        rootDomain: m.rootDomain,
        source: 'managed',
        managed: m,
        attached: !!m.attachedToCfPagesProject,
      });
    }

    // Second pass — project hostnames in use right now. A project's liveUrl
    // can be (a) a CF Pages subdomain (not a real custom domain), (b) the
    // staging subdomain, or (c) the production custom domain. We only count
    // the third case as "in use" for the Domains view.
    for (const sq of stateQueries) {
      const st = sq.data;
      if (!st?.liveUrl) continue;
      const host = st.liveUrl
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .toLowerCase();
      if (!host) continue;
      const isPagesSub = /\.pages\.dev$/.test(host);
      const isStagingSub = /\.sites\.[a-z0-9-]+\.[a-z]{2,}$/.test(host);
      if (isPagesSub || isStagingSub) continue;
      const root = host.replace(/^www\./, '');
      const existing = byHost.get(root);
      if (existing) {
        existing.inUseByProjectId = st.project.id;
        existing.inUseByProjectName =
          st.project.displayName || st.project.brandId;
      } else {
        byHost.set(root, {
          rootDomain: root,
          source: 'project',
          attached: true,
          inUseByProjectId: st.project.id,
          inUseByProjectName: st.project.displayName || st.project.brandId,
        });
      }
    }

    return Array.from(byHost.values()).sort((a, b) =>
      a.rootDomain.localeCompare(b.rootDomain),
    );
  }, [managedDomains, stateQueries]);

  // "Available" = any zone the operator owns that isn't actively serving a
  // production site. This includes pending-attach zones (just synced from
  // CF, not yet linked to a CF Pages project) — those still belong in the
  // "ready to pick for a site" bucket. The connect-domain endpoint
  // auto-attaches as part of the connect flow, so unattached !== blocked.
  const counts = {
    all: rows.length,
    available: rows.filter((r) => !r.inUseByProjectId).length,
    'in-use': rows.filter((r) => !!r.inUseByProjectId).length,
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = rows;
    if (tab === 'available') list = list.filter((r) => !r.inUseByProjectId);
    if (tab === 'in-use') list = list.filter((r) => !!r.inUseByProjectId);
    if (q) list = list.filter((r) => r.rootDomain.toLowerCase().includes(q));
    return list;
  }, [rows, tab, filter]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight2 text-zinc-900">Domains</h1>
          <p className="text-[13.5px] text-zinc-500 mt-1">
            Every Cloudflare zone you own — connected sites and available domains.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
          <Button
            variant="outline"
            size="md"
            onClick={handleSync}
            loading={syncing}
            leftIcon={!syncing ? <RefreshCw className="h-3.5 w-3.5" /> : undefined}
          >
            {syncing ? 'Syncing…' : 'Refresh from Cloudflare'}
          </Button>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter domains…"
              className="pl-8 h-9 text-[13px]"
            />
          </div>
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Total" value={counts.all} icon={<Cloud className="h-3.5 w-3.5" />} />
        <StatCard label="Available" value={counts.available} icon={<Check className="h-3.5 w-3.5" />} tone="green" />
        <StatCard label="In use" value={counts['in-use']} icon={<Globe className="h-3.5 w-3.5" />} tone="blue" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-200 overflow-x-auto">
        {(['all', 'available', 'in-use'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'relative h-9 px-3 text-[13px] font-medium transition-colors whitespace-nowrap',
              tab === t ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-800',
            )}
          >
            <span className="capitalize">{t.replace('-', ' ')}</span>
            <span className="ml-1.5 text-[11px] text-zinc-400">{counts[t]}</span>
            {tab === t && (
              <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-zinc-900" />
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Couldn't load domains</div>
            <div className="text-[12.5px] text-red-700/90 mt-0.5">{String(error)}</div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={<Cloud className="h-5 w-5" />}
          title="No domains here yet"
          description={
            tab === 'available'
              ? 'Every domain is currently in use. Add more zones in Cloudflare or disconnect one to free it up.'
              : tab === 'in-use'
                ? "No domains are connected to a site yet. Use the Connect domain button on a site to wire one up."
                : 'Add a domain in Cloudflare, then run the engine sync to surface it here.'
          }
        />
      )}

      {filtered.length > 0 && (
        <div className="bg-white border border-zinc-200/80 rounded-xl shadow-card divide-y divide-zinc-100 overflow-hidden">
          {filtered.map((row) => (
            <DomainListRow key={row.rootDomain} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone?: 'green' | 'blue';
}) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-[11.5px] uppercase tracking-wide text-zinc-400">{label}</div>
          <div className="text-2xl font-semibold tracking-tight2 text-zinc-900 mt-1 tabular-nums">
            {value}
          </div>
        </div>
        <div
          className={cn(
            'h-9 w-9 rounded-full flex items-center justify-center',
            tone === 'green' && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
            tone === 'blue' && 'bg-blue-50 text-blue-700 border border-blue-200',
            !tone && 'bg-zinc-100 text-zinc-500',
          )}
        >
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function DomainListRow({ row }: { row: DomainRow }) {
  const inUse = !!row.inUseByProjectId;
  const attached = row.attached;
  return (
    <div className="flex items-center gap-3 px-4 py-3 sm:py-3.5 hover:bg-zinc-50/60 transition-colors">
      <div
        className={cn(
          'h-9 w-9 shrink-0 rounded-lg flex items-center justify-center',
          inUse
            ? 'bg-blue-50 text-blue-600 border border-blue-200'
            : attached
              ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
              : 'bg-zinc-100 text-zinc-500 border border-zinc-200',
        )}
      >
        <Globe className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`https://${row.rootDomain}/`}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[13px] text-zinc-900 hover:underline truncate inline-flex items-center gap-1"
          >
            {row.rootDomain}
            <ExternalLink className="h-2.5 w-2.5 opacity-50" />
          </a>
          {inUse && (
            <Badge tone="blue" dot>
              In use
            </Badge>
          )}
          {!inUse && attached && (
            <Badge tone="green" dot>
              Available
            </Badge>
          )}
          {!attached && (
            <Badge tone="amber">
              <Clock className="h-2.5 w-2.5" />
              Pending attach
            </Badge>
          )}
          {row.managed?.kind === 'agency' && <Badge tone="neutral">Agency</Badge>}
        </div>
        <div className="mt-0.5 text-[11.5px] text-zinc-400 truncate">
          {row.inUseByProjectName && (
            <>
              Connected to <span className="text-zinc-600">{row.inUseByProjectName}</span>
              {row.managed?.cfZoneId && ' · '}
            </>
          )}
          {row.managed?.cfZoneId && (
            <>
              Zone <span className="font-mono">{row.managed.cfZoneId.slice(0, 8)}</span>
            </>
          )}
          {!row.managed && row.source === 'project' && (
            <span>Detected from live site (not yet synced as a CF zone)</span>
          )}
        </div>
      </div>
      <CopyHostnameButton text={row.rootDomain} />
    </div>
  );
}

function CopyHostnameButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Copy hostname"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* noop */
        }
      }}
      className={cn(copied && 'text-emerald-600')}
    >
      {copied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}
